import { useState, useCallback, useEffect, useRef } from "react";

/**
 * Hook for managing inline document rename state.
 *
 * Mirrors useFolderRename, with one extra responsibility: the user only edits
 * the basename — the file extension is preserved automatically when the rename
 * is sent to the API. This matches OS file managers (Finder, Explorer) which
 * also keep the extension out of the inline edit.
 *
 * The hook does NOT call the API directly. Pass an `onRename(id, newFilename)`
 * callback that resolves with the rename result; this hook owns the editing
 * state, the keyboard handlers, and the basename ↔ filename plumbing.
 */
export function useDocumentRename(
  onRename: (documentId: number, newFilename: string) => Promise<void> | void
) {
  // Document IDs are numeric in the schema, but DocumentRow types in the UI
  // sometimes carry them as strings. We normalize on number here.
  const [editingDocumentId, setEditingDocumentId] = useState<number | null>(null);
  const [editBasename, setEditBasename] = useState("");
  const [extension, setExtension] = useState("");
  const originalFilename = useRef<string>("");

  const startRename = useCallback((document: { id: number | string; filename: string }) => {
    const lastDot = document.filename.lastIndexOf(".");
    const hasExtension = lastDot > 0 && lastDot < document.filename.length - 1;
    const base = hasExtension ? document.filename.slice(0, lastDot) : document.filename;
    const ext = hasExtension ? document.filename.slice(lastDot) : "";

    setEditingDocumentId(typeof document.id === "string" ? parseInt(document.id, 10) : document.id);
    setEditBasename(base);
    setExtension(ext);
    originalFilename.current = document.filename;
  }, []);

  const confirmRename = useCallback(async () => {
    const trimmedBase = editBasename.trim();
    const id = editingDocumentId;
    const ext = extension;
    const original = originalFilename.current;

    setEditingDocumentId(null);
    setEditBasename("");
    setExtension("");
    originalFilename.current = "";

    if (id === null || trimmedBase.length === 0) return;

    const newFilename = `${trimmedBase}${ext}`;
    if (newFilename === original) return;

    await onRename(id, newFilename);
  }, [editBasename, editingDocumentId, extension, onRename]);

  const cancelRename = useCallback(() => {
    setEditingDocumentId(null);
    setEditBasename("");
    setExtension("");
    originalFilename.current = "";
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        void confirmRename();
      } else if (e.key === "Escape") {
        e.preventDefault();
        cancelRename();
      }
    },
    [confirmRename, cancelRename]
  );

  const isEditing = useCallback(
    (documentId: number | string) => {
      const numericId = typeof documentId === "string" ? parseInt(documentId, 10) : documentId;
      return editingDocumentId === numericId;
    },
    [editingDocumentId]
  );

  useEffect(() => {
    return () => {
      setEditingDocumentId(null);
      setEditBasename("");
      setExtension("");
      originalFilename.current = "";
    };
  }, []);

  return {
    editingDocumentId,
    editBasename,
    extension,
    startRename,
    confirmRename,
    cancelRename,
    handleKeyDown,
    setEditBasename,
    isEditing,
  };
}
